/**
 * Friendly schedule ↔ five-field cron.
 *
 * The backend still stores `cron` + `timezone`. This module is only for the
 * editor: parse a known expression into frequency / time / days, and write
 * one back. Anything we don't recognise becomes `custom` so a hand-written
 * cron is never silently rewritten.
 */

export const FREQUENCIES = [
  { value: 'every_15_min', label: 'Every 15 minutes' },
  { value: 'hourly', label: 'Every hour' },
  { value: 'daily', label: 'Every day' },
  { value: 'weekdays', label: 'Every weekday' },
  { value: 'weekly', label: 'Every week' },
  { value: 'monthly', label: 'Every month' },
  { value: 'custom', label: 'Custom' },
];

export const WEEKDAYS = [
  { value: 0, short: 'Sun', label: 'Sunday' },
  { value: 1, short: 'Mon', label: 'Monday' },
  { value: 2, short: 'Tue', label: 'Tuesday' },
  { value: 3, short: 'Wed', label: 'Wednesday' },
  { value: 4, short: 'Thu', label: 'Thursday' },
  { value: 5, short: 'Fri', label: 'Friday' },
  { value: 6, short: 'Sat', label: 'Saturday' },
];

export const HOURLY_MINUTES = [0, 15, 30, 45];

const FALLBACK_ZONES = [
  'UTC',
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Sao_Paulo',
  'Europe/London', 'Europe/Paris', 'Europe/Berlin',
  'Africa/Johannesburg',
  'Asia/Kolkata', 'Asia/Singapore', 'Asia/Tokyo', 'Asia/Shanghai',
  'Australia/Sydney', 'Pacific/Auckland',
];

export function detectedTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch (_) {
    return 'UTC';
  }
}

export function timezoneLabel(tz) {
  if (!tz) return 'UTC';
  if (tz === 'UTC' || tz === 'Etc/UTC') return 'UTC';
  const city = String(tz).split('/').slice(1).join(' / ').replace(/_/g, ' ');
  return city || tz;
}

export function timezoneOptions(current) {
  let zones = FALLBACK_ZONES;
  try {
    if (typeof Intl !== 'undefined' && typeof Intl.supportedValuesOf === 'function') {
      zones = Intl.supportedValuesOf('timeZone');
    }
  } catch (_) { /* keep fallback */ }
  const set = new Set(zones);
  if (current && !set.has(current)) set.add(current);
  set.add('UTC');

  const local = detectedTimezone();
  const grouped = new Map();
  for (const z of set) {
    if (z === 'UTC' || z === 'Etc/UTC') continue;
    const region = z.split('/')[0] || 'Other';
    if (!grouped.has(region)) grouped.set(region, []);
    const yours = z === local ? ' (your time)' : '';
    grouped.get(region).push({ value: z, label: `${timezoneLabel(z)}${yours}`, hint: z });
  }
  for (const opts of grouped.values()) {
    opts.sort((a, b) => a.label.localeCompare(b.label));
  }

  const common = [{ value: 'UTC', label: 'UTC' }];
  if (local && local !== 'UTC') {
    common.push({ value: local, label: `${timezoneLabel(local)} (your time)` });
  }
  const regions = [...grouped.keys()].sort((a, b) => a.localeCompare(b));
  return [
    { groupLabel: 'Common', options: common },
    ...regions.map((region) => ({
      groupLabel: region,
      options: grouped.get(region).filter((o) => o.value !== local),
    })).filter((g) => g.options.length > 0),
  ];
}

function isInt(s, min, max) {
  if (!/^\d+$/.test(s)) return false;
  const n = Number(s);
  return n >= min && n <= max;
}

function parseDow(field) {
  if (field === '1-5') return [1, 2, 3, 4, 5];
  if (field === '0-6' || field === '*') return [0, 1, 2, 3, 4, 5, 6];
  const days = [];
  for (const part of String(field).split(',')) {
    const p = part.trim();
    if (/^\d-\d$/.test(p)) {
      const a = Number(p[0]);
      const b = Number(p[2]);
      if (a > b || a < 0 || b > 7) return null;
      for (let i = a; i <= b; i += 1) days.push(i === 7 ? 0 : i);
    } else if (/^\d$/.test(p) || p === '7') {
      const n = Number(p) === 7 ? 0 : Number(p);
      if (n < 0 || n > 6) return null;
      days.push(n);
    } else {
      return null;
    }
  }
  return [...new Set(days)].sort((a, b) => a - b);
}

function sameDays(a, b) {
  if (a.length !== b.length) return false;
  return a.every((d, i) => d === b[i]);
}

/**
 * Empty / unknown cron → a daily 9:00 default.
 * `mode === 'custom'` keeps Custom selected even when the expression also
 * matches a preset (e.g. 0 9 * * *) — otherwise picking Custom looks like a no-op.
 */
export function parseCron(expr, mode) {
  const raw = String(expr || '').trim();
  const empty = {
    frequency: 'daily',
    hour: 9,
    minute: 0,
    days: [1],
    dayOfMonth: 1,
    cron: raw,
  };
  if (!raw) {
    return mode === 'custom' ? { ...empty, frequency: 'custom' } : empty;
  }

  const parts = raw.split(/\s+/);
  if (parts.length !== 5) {
    return { ...empty, frequency: 'custom', cron: raw };
  }
  const [min, hour, dom, month, dow] = parts;
  if (month !== '*') return { ...empty, frequency: 'custom', cron: raw };

  if ((min === '*/15' || min === '0,15,30,45') && hour === '*' && dom === '*' && dow === '*') {
    return finish({ ...empty, frequency: 'every_15_min', cron: raw }, mode);
  }
  if (isInt(min, 0, 59) && hour === '*' && dom === '*' && dow === '*') {
    return finish({ ...empty, frequency: 'hourly', minute: Number(min), cron: raw }, mode);
  }
  if (!isInt(min, 0, 59) || !isInt(hour, 0, 23)) {
    return { ...empty, frequency: 'custom', cron: raw };
  }
  const minute = Number(min);
  const hr = Number(hour);

  if (dom === '*' && dow === '*') {
    return finish({ ...empty, frequency: 'daily', hour: hr, minute, cron: raw }, mode);
  }
  if (dom === '*') {
    const days = parseDow(dow);
    if (!days) return { ...empty, frequency: 'custom', cron: raw };
    if (sameDays(days, [1, 2, 3, 4, 5])) {
      return finish({ ...empty, frequency: 'weekdays', hour: hr, minute, days, cron: raw }, mode);
    }
    return finish({ ...empty, frequency: 'weekly', hour: hr, minute, days, cron: raw }, mode);
  }
  if (dow === '*' && isInt(dom, 1, 31)) {
    return finish({ ...empty, frequency: 'monthly', hour: hr, minute, dayOfMonth: Number(dom), cron: raw }, mode);
  }
  return { ...empty, frequency: 'custom', cron: raw };
}

function finish(parsed, mode) {
  if (mode === 'custom') return { ...parsed, frequency: 'custom' };
  return parsed;
}

export function buildCron(state) {
  const minute = clamp(Number(state.minute) || 0, 0, 59);
  const hour = clamp(Number(state.hour) || 0, 0, 23);
  const dayOfMonth = clamp(Number(state.dayOfMonth) || 1, 1, 31);
  const days = (state.days && state.days.length ? state.days : [1])
    .map((d) => (Number(d) === 7 ? 0 : Number(d)))
    .filter((d) => d >= 0 && d <= 6);
  const uniqueDays = [...new Set(days)].sort((a, b) => a - b);

  switch (state.frequency) {
    case 'every_15_min':
      return '*/15 * * * *';
    case 'hourly':
      return `${minute} * * * *`;
    case 'daily':
      return `${minute} ${hour} * * *`;
    case 'weekdays':
      return `${minute} ${hour} * * 1-5`;
    case 'weekly':
      return `${minute} ${hour} * * ${uniqueDays.join(',')}`;
    case 'monthly':
      return `${minute} ${hour} ${dayOfMonth} * *`;
    case 'custom':
      return String(state.cron || '').trim();
    default:
      return `${minute} ${hour} * * *`;
  }
}

export function isValidCron(expr) {
  const parts = String(expr || '').trim().split(/\s+/);
  return parts.length === 5 && parts.every((p) => p.length > 0);
}

export function formatClock(hour, minute) {
  const h = clamp(Number(hour) || 0, 0, 23);
  const m = clamp(Number(minute) || 0, 0, 59);
  const suffix = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, '0')} ${suffix}`;
}

function ordinal(n) {
  const v = Number(n);
  const rem = v % 100;
  if (rem >= 11 && rem <= 13) return `${v}th`;
  switch (v % 10) {
    case 1: return `${v}st`;
    case 2: return `${v}nd`;
    case 3: return `${v}rd`;
    default: return `${v}th`;
  }
}

function dayNames(days) {
  const set = new Set(days);
  return WEEKDAYS.filter((d) => set.has(d.value)).map((d) => d.label);
}

export function describeSchedule(state, timezone) {
  const tz = timezoneLabel(timezone || 'UTC');
  const time = formatClock(state.hour, state.minute);
  const inTz = tz === 'UTC' ? 'UTC' : tz;
  switch (state.frequency) {
    case 'every_15_min':
      return `Runs every 15 minutes (${inTz})`;
    case 'hourly': {
      const mm = String(clamp(Number(state.minute) || 0, 0, 59)).padStart(2, '0');
      return `Runs every hour at :${mm} (${inTz})`;
    }
    case 'daily':
      return `Runs every day at ${time} (${inTz})`;
    case 'weekdays':
      return `Runs Monday–Friday at ${time} (${inTz})`;
    case 'weekly': {
      const names = dayNames(state.days && state.days.length ? state.days : [1]);
      const when = names.length === 1 ? names[0]
        : names.length === 2 ? `${names[0]} and ${names[1]}`
        : `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
      return `Runs every ${when} at ${time} (${inTz})`;
    }
    case 'monthly':
      return `Runs on the ${ordinal(state.dayOfMonth || 1)} of each month at ${time} (${inTz})`;
    case 'custom':
      return state.cron ? `Runs on ${state.cron} (${inTz})` : 'Set when this runs.';
    default:
      return `Runs at ${time} (${inTz})`;
  }
}

function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}
